import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { PageLayout } from '@components/layout/PageLayout'
import { Card, CardContent, CardHeader, CardTitle } from '@components/ui/Card'
import { Input } from '@components/ui/Input'
import { Pagination } from '@components/ui/Pagination'
import { Button } from '@components/ui/Button'
import { SegmentedButton } from '@components/ui/SegmentedButton'
import { ToastContainer } from '@components/ui/Toast'
import { ConfirmDialog } from '@components/ui/ConfirmDialog'
import { useToast } from '@hooks/useToast'
import { useProducts } from '@hooks/useProducts'
import { useDebounce } from '@hooks/useDebounce'
import type { Product } from '../../types/product'
import { 
  PlusIcon, 
  ArrowPathIcon, 
  PencilIcon, 
  TrashIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline'

export const ProductsPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { 
    products, 
    isLoading, 
    error, 
    filters,
    setFilters, 
    resetFilters,
    deleteProduct,
    refetch,
  } = useProducts()
  
  const [searchQuery, setSearchQuery] = useState(filters.query || '')
  const [sortColumn, setSortColumn] = useState<string | null>(null)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [productToDelete, setProductToDelete] = useState<Product | null>(null)
  const { toasts, showToast, removeToast } = useToast()
  const processedStateRef = useRef(false)
  
  const debouncedSearch = useDebounce(searchQuery, 500)

  // Apply filter from navigation state on mount
  useEffect(() => {
    // Process location state only if it exists and hasn't been processed yet
    if (!location.state || processedStateRef.current) return
    
    // Mark as processed to prevent double execution in StrictMode
    processedStateRef.current = true
    
    if (location.state.toValidate === true) {
      setFilters({ toValidate: true, page: 1 })
      refetch()
    }
    // Trigger refresh if requested (but not if we already did it for toValidate)
    else if (location.state.triggerRefresh) {
      refetch()
    }
    
    // Show toast if coming from delete action
    if (location.state.showToast) {
      showToast(location.state.toastMessage, location.state.toastType)
    }
    
    // Clear the state after processing
    navigate(location.pathname, { replace: true, state: {} })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only run once on mount

  useEffect(() => {
    // Only search if we have 0 characters (reset) or at least 3 characters
    if (searchQuery.length === 0 || searchQuery.length >= 3) {
      if (debouncedSearch !== filters.query) {
        setFilters({ query: debouncedSearch, page: 1 })
        refetch()
      }
    }
  }, [debouncedSearch, filters.query, searchQuery.length, setFilters, refetch])

  // Listen for product updates from detail pages
  useEffect(() => {
    const handleProductUpdate = () => {
      refetch()
    }

    window.addEventListener('productUpdated', handleProductUpdate)
    return () => {
      window.removeEventListener('productUpdated', handleProductUpdate)
    }
  }, [refetch])

  const handleSort = (column: string) => {
    let newDirection: 'asc' | 'desc' = 'asc'
    
    if (sortColumn === column) {
      newDirection = sortDirection === 'asc' ? 'desc' : 'asc'
    }
    
    setSortColumn(column)
    setSortDirection(newDirection)
    
    const sortBy = newDirection === 'desc' ? `-${column}` : column
    setFilters({ sortBy })
    refetch()
  }

  const handleDelete = async (product: Product) => {
    setProductToDelete(product)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (productToDelete?.uuid) {
      try {
        await deleteProduct(productToDelete.uuid)
        setProductToDelete(null)
        // Toast will be shown from navigation state when deleting from details page
        // or we show it here when deleting from the list
        if (!location.state?.showToast) {
          showToast('Produit supprimé avec succès', 'success')
        }
      } catch (error) {
        console.error('Failed to delete product:', error)
        showToast('Erreur lors de la suppression du produit', 'error')
      }
    }
  }

  const handleEdit = (product: Product) => {
    if (product.uuid) {
      navigate(`/products/${product.uuid}`)
    }
  }

  const handleStatusFilter = (toValidate: boolean | null) => {
    setFilters({ toValidate, page: 1 })
    refetch()
  }

  // Check if any filters are active
  const hasActiveFilters = () => {
    return searchQuery.trim() !== '' || filters.toValidate !== null
  }

  const SortIcon = ({ column }: { column: string }) => {
    if (sortColumn !== column) {
      return <div className="w-4 h-4" />
    }
    return sortDirection === 'asc' 
      ? <ChevronUpIcon className="w-4 h-4" />
      : <ChevronDownIcon className="w-4 h-4" />
  }

  console.log('ProductsPage render:', { 
    isLoading, 
    error, 
    productsCount: products?.data?.length,
    filters 
  })

  return (
    <PageLayout>
      <ToastContainer toasts={toasts} onClose={removeToast} />
      
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-semibold text-gray-900 dark:text-white">Produits</h1>
        <Button 
          onClick={refetch}
          variant="outline"
          className="flex items-center gap-2"
        >
          <ArrowPathIcon className="h-4 w-4" />
          Rafraîchir
        </Button>
      </div>

      {/* Filters Card */}
      <Card className="mb-6">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="flex items-center gap-2">
                  <FunnelIcon className="h-5 w-5" />
                  Filtres
                </CardTitle>
                <Button 
                  onClick={() => navigate('/products/new')}
                  className="flex items-center gap-2"
                >
                  <PlusIcon className="h-4 w-4" />
                  Nouveau produit
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="relative">
                <Input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Rechercher un produit... (min. 3 caractères)"
                  className="w-full pl-10"
                />
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                  <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <SegmentedButton
                  options={[
                    { value: null, label: 'Tous' },
                    { value: true, label: 'À catégoriser' },
                    { value: false, label: 'Catégorisés' }
                  ]}
                  value={filters.toValidate}
                  onChange={(value) => handleStatusFilter(value as boolean | null)}
                />
                
                <div className="flex items-center gap-4">
                  {products && (
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {products.totalRecords} produit(s)
                    </span>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSearchQuery('')
                      resetFilters()
                      refetch()
                    }}
                    disabled={!hasActiveFilters()}
                  >
                    Effacer les filtres
                  </Button>
                </div>
              </div>
            </CardContent>
      </Card>

      {/* Products Table */}
      <Card>
            <CardContent className="p-0">
              {error && (
                <div className="p-4 text-red-600 dark:text-red-400 text-center">
                  Erreur lors du chargement des produits : {error}
                </div>
              )}
              
              {isLoading && (
                <div className="p-8 text-center">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white"></div>
                  <p className="mt-2 text-gray-600 dark:text-gray-400">Chargement des produits...</p>
                </div>
              )}
              
              {!isLoading && !error && products && (
                <>
                  {products.data.length === 0 ? (
                    <div className="p-8 text-center text-gray-600 dark:text-gray-400">
                      Aucun produit trouvé
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50 dark:bg-gray-800 border-b dark:border-gray-700">
                          <tr>
                            <th 
                              className="px-6 py-3 text-left cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                              onClick={() => handleSort('brandName')}
                            >
                              <div className="flex items-center gap-2 text-xs font-normal text-gray-900 dark:text-white uppercase tracking-wider">
                                Marque
                                <SortIcon column="brandName" />
                              </div>
                            </th>
                            <th 
                              className="px-6 py-3 text-left cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                              onClick={() => handleSort('categoryName')}
                            >
                              <div className="flex items-center gap-2 text-xs font-normal text-gray-900 dark:text-white uppercase tracking-wider">
                                Catégorie
                                <SortIcon column="categoryName" />
                              </div>
                            </th>
                            <th 
                              className="px-6 py-3 text-left cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                              onClick={() => handleSort('toValidate')}
                            >
                              <div className="flex items-center gap-2 text-xs font-normal text-gray-900 dark:text-white uppercase tracking-wider">
                                Statut
                                <SortIcon column="toValidate" />
                              </div>
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-normal text-gray-900 dark:text-white uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                          {products.data.map((product) => (
                            <tr 
                              key={product.uuid}
                              className="hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                              onClick={() => handleEdit(product)}
                            >
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                {product.brandName || '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                {product.categoryName || '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                  product.toValidate 
                                    ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                    : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                }`}>
                                  {product.toValidate ? 'À catégoriser' : 'Catégorisé'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                <div className="flex gap-2">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleEdit(product)
                                    }}
                                    className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300"
                                  >
                                    <PencilIcon className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleDelete(product)
                                    }}
                                    className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  
                  {/* Pagination */}
                  {products && products.data.length > 0 && (
                    <div className="p-4 border-t dark:border-gray-700">
                      <Pagination
                        currentPage={products.currentPage}
                        totalPages={products.lastPage}
                        pageSize={filters.limit}
                        totalRecords={products.totalRecords}
                        onPageChange={(page) => {
                          setFilters({ page })
                          refetch()
                        }}
                        onPageSizeChange={(limit) => {
                          setFilters({ limit, page: 1 })
                          refetch()
                        }}
                      />
                    </div>
                  )}
                </>
              )}
            </CardContent>
      </Card>

      {/* Delete Confirmation Modal */}
      <ConfirmDialog
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false)
          setProductToDelete(null)
        }}
        onConfirm={confirmDelete}
        title="Confirmer la suppression"
        message={`Êtes-vous sûr de vouloir supprimer le produit "${productToDelete?.brandName || ''}" ? Cette action est irréversible.`}
        confirmText="Supprimer"
        cancelText="Annuler"
      />
    </PageLayout>
  )
}